import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ServicePathProviderService } from '@blk/cwp-ng-framework';
import { ModelAnalyticsAPIComputeModelFamilyAnalyticsRequest, V1ModelFamilyAnalytics } from '@blk/portfolio-security-server/analytics/model';
import { V1ListModelFamilyRequest, Wealthdistributionmodelsv1BatchGetModelsRequest, Wealthdistributionmodelsv1BatchGetModelsResponse } from '@blk/portfolio-security-server/models';
import { InjectConfig, LocaleUtilResolverService, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { PasDataServiceApiConstants, PasRcResolverService } from '@ddt-web/data-service';
import { InjectQueryParam } from '@ddt-web/generic-utils';
import { PasApiService } from '@ddt-web/pas';
import { PartnerQueryParamKey, SharedQueryParamKey } from '@ddt-web/url';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { LocaleToPkgEnum } from '../enums';

@Injectable({
	providedIn: 'root'
})
export class ModelsBrowseApiService {
	@InjectConfig(ModelsExperienceConfigKey.ME_AS_OF_DATE) private readonly meAsOfDate = [];
	@InjectConfig(ModelsExperienceConfigKey.CACHE_BUSTER_ENFORCED) private readonly cacheBusterEnforced = '';
	@InjectQueryParam(SharedQueryParamKey.PAS_PREVIEW) private readonly preview: string;
	@InjectQueryParam(SharedQueryParamKey.PAS_AS_OF_DATE) private readonly pasAsOfDate: string;

	private readonly API_GATEWAY: string = 'api-gateway';
	private readonly API_BASE: string = '/iretire-server/services/v1';
	private readonly API_BASE_V2: string = '/iretire-server/services/v2'; // Post ADAM migration
	private readonly API_BASE_V21: string = '/iretire-server/services/v2.1';

	private readonly API = {
		FAMILIES: '/model-families',
		FAMILY: '/model-family/'
	};

	private readonly FAMILY_API = {
		HOLDINGS: '/holdings',
		OVERVIEW: '/overview',
		PERFORMANCE: '/performance'
	};

	private locale: string;

	constructor(
		private http: HttpClient,
		private servicePathProvider: ServicePathProviderService,
		private pasApiService: PasApiService,
		private pasRcResolverService: PasRcResolverService,
		private localeUtilResolverService: LocaleUtilResolverService
	) { }

	/**
	 * Fetch model families list from iRetire server
	 *
	 * @returns {Observable<any>}
	 * @memberof ModelsBrowseApiService
	 */
	public fetchFamiliesFromIretireServer(): Observable<any> {
		const url: string = this.getApiServicePath(this.API.FAMILIES, this.API_BASE_V2);
		return this.http.get(url);
	}

	/**
	 * Fetch model families list in a public mode (NODMZ solution)
	 *
	 * @returns {Observable<any>}
	 * @memberof ModelsBrowseApiService
	 */
	public fetchFamiliesInPublicMode(url: string): Observable<any> {
		const cacheBusterUrl: string = this.cacheBusterEnforced ? url + `?cacheBuster=${this.cacheBusterEnforced}` : url;
		return this.http.get(cacheBusterUrl);
	}

	public fetchFamiliesFromIretireServerV21(): Observable<any> {
		const locale = this.localeUtilResolverService.getResolvedLocaleValue();
		const url: string = this.getApiServicePath(this.API.FAMILIES, this.API_BASE_V21) + `?pkg=${LocaleToPkgEnum[locale]}`;
		return this.http.get(url);
	}

	/**
	 * Fetch model families list from PAS server
	 *
	 * @returns {Observable<any>}
	 * @memberof ModelsBrowseApiService
	 */
	public fetchFamilies(requestParams: V1ListModelFamilyRequest): Observable<any> {
		return this.pasApiService.fetchModelFamilies(requestParams);
	}

	/**
	 * Fetch family holdings list from iRetire server
	 *
	 * @param {string} familyId
	 * @returns {Observable<any>}
	 * @memberof ModelsBrowseApiService
	 */
	public fetchFamilyHoldings(modelIds: Array<string>, subType: string): Observable<Wealthdistributionmodelsv1BatchGetModelsResponse> {
		const asOfDate = this.computeAsOfDate(subType);
		const preview = this.preview === 'true';
		this.locale = this.localeUtilResolverService.getResolvedLocaleValue();
		return this.pasRcResolverService.getRcForUserType(PasDataServiceApiConstants.MODEL_DETAILS, this.locale).pipe(
			map((rc: string) => ({
				modelIds, rc, preview, asOfDate
			} as Wealthdistributionmodelsv1BatchGetModelsRequest)),
			switchMap((requestParams: Wealthdistributionmodelsv1BatchGetModelsRequest) => this.pasApiService.fetchModelHoldings(requestParams))
		);
	}

	public fetchFamilyModelsAnalytics(familyId: string, customAdvisoryFee: number, subType: string): Observable<V1ModelFamilyAnalytics> {
		const asOfDate = this.computeAsOfDate(subType);
		const feeOverride = customAdvisoryFee ? (customAdvisoryFee / 100) : null;
		const simulatedPerformanceUserInput = feeOverride ? { feeOverride } : null;
		const testPartnerFirm = this.getTestPartnerFirm();
		const preview = this.preview === 'true';
		this.locale = this.localeUtilResolverService.getResolvedLocaleValue();
		const currencyCode = PasDataServiceApiConstants.LOCALE_CURRENCY_MAPPING[this.locale];
		return this.pasRcResolverService.getRcForUserType(PasDataServiceApiConstants.PERFORMANCE, this.locale)
			.pipe(
				map((rc: string) => ({
					currencyCode, rc, simulatedPerformanceUserInput, testPartnerFirm, asOfDate, preview
				} as ModelAnalyticsAPIComputeModelFamilyAnalyticsRequest)),
				switchMap((requestParams: ModelAnalyticsAPIComputeModelFamilyAnalyticsRequest) => this.pasApiService.fetchModelsAnalytics(familyId, requestParams))
			);
	}

	public fetchFamilyModelsEsgAnalytics(familyId: string, subType: string): Observable<V1ModelFamilyAnalytics> {
		const asOfDate = this.computeAsOfDate(subType);
		this.locale = this.localeUtilResolverService.getResolvedLocaleValue();
		const testPartnerFirm = this.getTestPartnerFirm();
		const preview = this.preview === 'true';
		const currencyCode = PasDataServiceApiConstants.LOCALE_CURRENCY_MAPPING[this.locale];
		return this.pasRcResolverService.getRcForUserType(PasDataServiceApiConstants.ESG, this.locale)
			.pipe(
				map((rc: string) => ({
					currencyCode, rc, testPartnerFirm, preview, asOfDate
				} as ModelAnalyticsAPIComputeModelFamilyAnalyticsRequest)),
				switchMap((requestParams: ModelAnalyticsAPIComputeModelFamilyAnalyticsRequest) => this.pasApiService.fetchModelsAnalytics(familyId, requestParams))
			);
	}

	/**
	 * Get API service path
	 *
	 * @private
	 * @param {string} path
	 * @returns {string}
	 * @memberof ModelsBrowseApiService
	 */
	private getApiServicePath(path: string, apiPath: string = ''): string {
		const apiBase = apiPath || this.API_BASE;
		let url = this.servicePathProvider.getServicePath(this.API_GATEWAY) + apiBase + path;
		const parsedUrl = new URL(window.location.href);
		const testPartnerFirm = parsedUrl.searchParams.get(PartnerQueryParamKey.TEST_PARTNER_FIRM);
		if (testPartnerFirm) {
			url += '?testPartnerFirm=' + testPartnerFirm;
		}
		return url;
	}

	private getTestPartnerFirm(): string {
		const parsedUrl = new URL(window.location.href);
		const testPartnerFirm = parsedUrl.searchParams.get(PartnerQueryParamKey.TEST_PARTNER_FIRM);
		return testPartnerFirm;
	}

	private computeAsOfDate(subType: string): string {
		const meConfigAsOfDate = this.meAsOfDate?.[0];
		const asOfDate = this.pasAsOfDate ?? meConfigAsOfDate?.[`${subType ?? 'BLK'}_AS_OF_DATE`];
		return asOfDate;
	}

}
